import { forwardRef } from 'react';
import { cn } from '@/lib/utils/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
export type ButtonSize    = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:   'bg-[var(--color-accent-primary)] text-white hover:opacity-90 border border-transparent',
  secondary: 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-primary)] border border-[var(--color-border)] hover:border-[var(--color-text-secondary)]',
  ghost:     'bg-transparent text-[var(--color-text-secondary)] border border-transparent hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]',
  danger:    'bg-[color-mix(in_srgb,var(--color-error)_15%,transparent)] text-[var(--color-error)] border border-[color-mix(in_srgb,var(--color-error)_30%,transparent)] hover:bg-[color-mix(in_srgb,var(--color-error)_25%,transparent)]',
  outline:   'bg-transparent text-[var(--color-text-primary)] border border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs rounded-[var(--radius-sm)] min-h-8 gap-1.5',
  md: 'px-4 py-2.5 text-sm rounded-[var(--radius-md)] min-h-11 gap-2',
  lg: 'px-5 py-3   text-base rounded-[var(--radius-md)] min-h-12 gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size    = 'md',
      loading = false,
      iconLeft,
      iconRight,
      fullWidth = false,
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading}
        className={cn(
          'inline-flex items-center justify-center font-medium transition-all',
          'focus-visible:outline-2 focus-visible:outline-[var(--color-accent-primary)] focus-visible:outline-offset-2',
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          isDisabled && 'opacity-50 cursor-not-allowed pointer-events-none',
          className,
        )}
        {...props}
      >
        {loading ? (
          <span
            className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin flex-shrink-0"
            aria-hidden="true"
          />
        ) : (
          iconLeft && <span className="flex-shrink-0" aria-hidden="true">{iconLeft}</span>
        )}
        {children}
        {!loading && iconRight && (
          <span className="flex-shrink-0" aria-hidden="true">{iconRight}</span>
        )}
      </button>
    );
  }
);
Button.displayName = 'Button';
